import { LineChart, hasChartData } from '@frontend/chart/components';
import { ChartInfoKind } from '@frontend/chart/types';
import { CommonMessage } from '@frontend/lang';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { useInsightChartMinHeight } from '../insight-detail-mode.context';
import { InsightId } from '../insight-registry/insight-ids';
import { usePudoDailyTurnoverRateLineChart } from './use-pudo-daily-turnover-rate-line-chart';

export interface PudoDailyTurnoverRateLineChartProps {
    accountId: string;
    width?: InsightCardWidth;
}

export const PudoDailyTurnoverRateLineChart = ({ accountId, width }: PudoDailyTurnoverRateLineChartProps) => {
    const chartMinHeight = useInsightChartMinHeight(280);
    const { data, isLoading } = usePudoDailyTurnoverRateLineChart(accountId);
    const { formatMessage } = useIntl();

    const chartData = useMemo(() => {
        if (!data) return undefined;
        const seriesName = formatMessage({
            id: 'platform.analytics.insights.pudo-daily-turnover-rate-line-chart.series',
            description: 'Series name for the PuDo daily turnover rate line chart on analytics insights',
            defaultMessage: 'Daily turnover rate'
        });
        return {
            series: data.series.map((series) => ({ ...series, name: seriesName }))
        };
    }, [data, formatMessage]);

    const cyclesPerDayLabel = formatMessage({
        id: 'platform.analytics.insights.pudo-daily-turnover-rate-line-chart.y-axis',
        description: 'Y-axis label for the PuDo daily turnover rate line chart on analytics insights',
        defaultMessage: 'Cycles per day'
    });

    return (
        <InsightCard
            insightId={InsightId.PUDO_DAILY_TURNOVER_RATE}
            infoKind={ChartInfoKind.DAILY_TURNOVER_RATE_LINE}
            width={width}
            title={
                <FormattedMessage
                    id='platform.analytics.insights.pudo-daily-turnover-rate-line-chart.title'
                    description='Title for the PuDo daily turnover rate line chart on analytics insights'
                    defaultMessage='Daily turnover rate'
                />
            }
            isLoading={isLoading}>
            {!hasChartData(chartData) || !chartData ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : (
                <div className='w-full min-w-0'>
                    <LineChart
                        data={chartData}
                        minHeight={chartMinHeight}
                        tooltipShared
                        chartOptions={{
                            yaxis: {
                                title: {
                                    text: cyclesPerDayLabel
                                },
                                decimalsInFloat: 2
                            }
                        }}
                    />
                </div>
            )}
        </InsightCard>
    );
};
